/**
 * Game Setup Screen — Host picks deck, timer, rounds, player order.
 */

import { getState, updateSettings, selectDeck, startGame } from '../game-state';
import { standardDecks } from '../../data';
import { navigate } from '../router';
import { startCustomDeckCreation } from './custom-deck';
import { t, tDeck } from '../i18n';

export function renderSetup(): void {
  const app = document.getElementById('app')!;
  const state = getState();
  const settings = state.settings;

  app.innerHTML = `
    <h1>${t('gameSetup')}</h1>
    <p class="subtitle">${t('setupSubtitle')}</p>

    <div class="stack-lg">
      <!-- Deck Selection -->
      <div class="setting-group">
        <p class="setting-label">${t('chooseDeck')}</p>
        <div class="stack-sm" id="deck-list">
          ${standardDecks.map((deck) => `
            <label class="deck-option ${settings.deckId === deck.id ? 'selected' : ''}" data-deck-id="${deck.id}">
              <input type="radio" name="deck" value="${deck.id}"
                ${settings.deckId === deck.id ? 'checked' : ''} />
              <div class="deck-meta">
                <div class="deck-name">${deck.name}</div>
                <div class="deck-desc">${tDeck(deck.id, deck.description)} (${deck.cards.length} ${t('cards')})</div>
              </div>
            </label>
          `).join('')}
          <label class="deck-option ${settings.deckId === 'custom' ? 'selected' : ''}" data-deck-id="custom">
            <input type="radio" name="deck" value="custom"
              ${settings.deckId === 'custom' ? 'checked' : ''} />
            <div class="deck-meta">
              <div class="deck-name">${t('customDeck')}</div>
              <div class="deck-desc">${t('customDeckDesc')}</div>
            </div>
          </label>
        </div>
      </div>

      <!-- Timer -->
      <div class="setting-group">
        <p class="setting-label">${t('timerPerTurn')}</p>
        <div class="toggle-group">
          <button class="toggle-btn ${settings.timerSeconds === 30 ? 'active' : ''}" data-timer="30">30s</button>
          <button class="toggle-btn ${settings.timerSeconds === 45 ? 'active' : ''}" data-timer="45">45s</button>
          <button class="toggle-btn ${settings.timerSeconds === 60 ? 'active' : ''}" data-timer="60">60s</button>
          <button class="toggle-btn ${settings.timerSeconds === 90 ? 'active' : ''}" data-timer="90">90s</button>
        </div>
      </div>

      <!-- Cards Per Player -->
      <div class="setting-group">
        <p class="setting-label">${t('cardsPerPlayer')}</p>
        <div class="toggle-group">
          <button class="toggle-btn ${settings.cardsPerPlayer === 4 ? 'active' : ''}" data-cpp="4">4</button>
          <button class="toggle-btn ${settings.cardsPerPlayer === 5 ? 'active' : ''}" data-cpp="5">5</button>
          <button class="toggle-btn ${settings.cardsPerPlayer === 6 ? 'active' : ''}" data-cpp="6">6</button>
          <button class="toggle-btn ${settings.cardsPerPlayer === 7 ? 'active' : ''}" data-cpp="7">7</button>
        </div>
        <p class="text-muted" style="font-size: var(--font-size-xs); margin-top: var(--space-xs);">
          ${t('totalDeck', state.players.length, settings.cardsPerPlayer, state.players.length * settings.cardsPerPlayer)}
        </p>
      </div>

      <!-- Player Order -->
      <div class="setting-group">
        <p class="setting-label">${t('playerOrder')}</p>
        <div class="toggle-group">
          <button class="toggle-btn ${settings.playerOrder === 'shuffle' ? 'active' : ''}" data-order="shuffle">${t('shuffle')}</button>
          <button class="toggle-btn ${settings.playerOrder === 'define' ? 'active' : ''}" data-order="define">${t('define')}</button>
        </div>
      </div>

      <!-- Drawing Round -->
      <label class="checkbox-row">
        <input type="checkbox" id="drawing-toggle"
          ${settings.drawingRoundEnabled ? 'checked' : ''} />
        ${t('enableRound4')}
      </label>

      <!-- Error -->
      <p id="setup-error" class="error-text" style="min-height: 1.2em;"></p>

      <!-- Actions -->
      <button class="btn btn-primary" id="btn-confirm">${t('confirmAndPlay')}</button>
      <button class="btn btn-tertiary" id="btn-back">${t('backToLobby')}</button>
    </div>
  `;

  attachSetupListeners();
}

function attachSetupListeners(): void {
  // Deck selection
  document.querySelectorAll('.deck-option').forEach((option) => {
    option.addEventListener('click', () => {
      const deckId = (option as HTMLElement).dataset.deckId!;
      if (deckId === 'custom') {
        updateSettings({ deckId: 'custom' });
        renderSetup();
      } else {
        const deck = standardDecks.find((d) => d.id === deckId);
        if (deck) {
          selectDeck(deck);
          renderSetup();
        }
      }
    });
  });

  // Timer toggles
  document.querySelectorAll('[data-timer]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const seconds = parseInt((btn as HTMLElement).dataset.timer!) as 30 | 45 | 60 | 90;
      updateSettings({ timerSeconds: seconds });
      renderSetup();
    });
  });

  // Cards per player toggles
  document.querySelectorAll('[data-cpp]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const cpp = parseInt((btn as HTMLElement).dataset.cpp!) as 4 | 5 | 6 | 7;
      updateSettings({ cardsPerPlayer: cpp });
      renderSetup();
    });
  });

  // Player order toggles
  document.querySelectorAll('[data-order]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const order = (btn as HTMLElement).dataset.order as 'shuffle' | 'define';
      updateSettings({ playerOrder: order });
      renderSetup();
    });
  });

  // Drawing round toggle
  document.getElementById('drawing-toggle')?.addEventListener('change', (e) => {
    const checked = (e.target as HTMLInputElement).checked;
    updateSettings({ drawingRoundEnabled: checked });
  });

  // Confirm & Play
  document.getElementById('btn-confirm')?.addEventListener('click', () => {
    const state = getState();
    if (state.settings.deckId === 'custom') {
      // Go to custom deck creation flow
      startCustomDeckCreation();
      navigate('custom-deck');
      return;
    }
    const error = startGame();
    if (error) {
      document.getElementById('setup-error')!.textContent = error;
      return;
    }
    navigate('round-interstitial');
  });

  // Back
  document.getElementById('btn-back')?.addEventListener('click', () => navigate('lobby'));
}
